// /src/pages/noCustomerInvoices.js
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { resolveIcon } from "../../../utils/resolveIcon";

// Styles
import classes from "../../../components/Modal.module.css";
import classes3 from "../../../components/PDFUploader.module.css";
import "../../InvoicesManagement.scss";

// Page sections
import NoCustomerInformationModal from "./NoCustomerInformationModal";

// Utils
import { UserAuth } from "../../../utils/AuthContext";
import { useDocs } from "../../../utils/useDocs";
import { DataContext } from "../../../utils/DataContext";
import useLanguage from "../../../utils/useLanguage";
import { formatDate } from "../../../utils/toDate";
import { toMoney } from "../../../utils/toMoney";

// Components
import Dropdown from "../../../components/Dropdown";
import BackToDashboard from "../../../components/BackToDashboard";
import Loader from "../../../components/Loader";
import CustomLink from "../../../components/CustomLink";
import UserSection from "../../../components/UserSection";
import InformationBox from "../../../components/InformationBox";
import MultipleSearchFilter from "../../../components/MultipleSearchFilter";
import ChartModal from "../../../components/ChartModal";
import CustomButton from "../../../components/CustomButton";
import AllDocumentsSlider from "../../../components/AllDocumentsSlider";
import LoaderWithProgress from "../../../components/LoaderWithProgress";
import { useSearchParams, useLocation } from "react-router-dom";

// Import the new service
import {
  getMultiContainerInvoices,
  getArchivedMultiContainerInvoicesWithouC,
} from "../../../services/invoices/multiContainerWithoutC"; // Adjust path as needed
import ErrorMessage from "../../../components/ErrorMessage";
import ErrorBox from "../../../components/ErrorBox";

const NoCustomerInvoices = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // New state variables for the row modal
  const [selectedRow, setSelectedRow] = useState(null);
  const [showRowModal, setShowRowModal] = useState(false);

  const [pdfFile, setPdfFile] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [extractedData, setExtractedData] = useState({});
  const [isAnalyzable, setIsAnalyzable] = useState(false);

  const [docsFields, setDocsFields] = useState({
    count: 1,
    max: 10,
  });
  const [tempDocs, setTempDocs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showBtns, setShowBtns] = useState(false);

  const { handlePrepareDocs } = useDocs();

  const { userData } = useContext(UserAuth);

  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [balances, setBalances] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [invoicesTotalPages, setInvoicesTotalPages] = useState(1);
  const [invoicesCurrentPage, setInvoicesCurrentPage] = useState(1);
  const [error, setError] = useState("");
  const [showCantAddInvoice, setShowCantAddInvoice] = useState(false);

  // ✅ keep only refs needed for "single useEffect" behavior
  const didInitPageFromUrl = useRef(false);
  const skipNextFetchRef = useRef(false);

  const navigate = useNavigate();

  const show3Buttons = () => {
    setShowBtns(true);
  };

  // function to delete the files preupload
  const handleDeleteFile = (index) => {
    cancelUpload();
    setTempDocs((prev) => {
      const newDocs = [...prev];
      newDocs.splice(index, 1); // Remove the file at the given index
      return newDocs;
    });

    // resetting the input element to empty
    const fileInput = document.getElementById(`invoiceDoc${index + 1}`);
    if (fileInput) {
      fileInput.value = "";
    }

    setPdfFile(null);
    setIsAnalyzable(false);
    setAnalyzeLoading(false);
  };

  const generateUploadInputs = () => {
    return Array.from({ length: docsFields.count }).map((_, index) => (
      <>
        <form className="o-column" key={index}>
          <div className="o-row o-row--vertical-align_center o-row--gap-size_400">
            <input
              accept="application/pdf, image/*"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                setPdfFile(file);
                handlePrepareDocs(
                  e,
                  // `Invoice ${invoiceNumber} - image ${index + 1}`
                  `image ${index + 1}`,
                );
                setTempDocs((prev) => [...prev, file]);

                // Enable the Analyze button
                setIsAnalyzable(true);
              }}
              className={classes3.fileInput}
              name="images"
              id={`invoiceDoc${index + 1}`}
              disabled={tempDocs.length > 0}
            />
            <div>
              <label htmlFor={`invoiceDoc${index + 1}`}>
                <p
                  // className={classes3.customFileInput}
                  className={`${
                    tempDocs.length > 0 ? classes3.buttonDisabled : ""
                  } ${classes3.customFileInput}`}
                >
                  <img
                    src={resolveIcon("upload")}
                    alt={`Upload image ${index + 1}`}
                  />
                  <span>
                    {t("uploadDocument")} {index + 1}
                  </span>
                </p>
              </label>
            </div>

            <CustomButton
              title={t("analyzeInvoice")}
              icon="analyze"
              variant="blank"
              onClick={handleUpload}
              type="submit"
              disabled={!isAnalyzable}
            />

            <CustomButton
              title={t("cancel")}
              variant="blank"
              icon="close"
              iconSize={14}
              onClick={() => {
                setShowBtns(false);
                handleDeleteFile(index);
              }}
            />
          </div>
          {analyzeLoading && (
            <LoaderWithProgress loadingText="analyzingInvoice" />
          )}
          {tempDocs[index] && (
            <div className={classes3.fileName}>
              {t("fileName")} : {tempDocs[index]?.name?.slice(0, 30)}
              <button
                className={classes3.deleteFileButton}
                onClick={() => handleDeleteFile(index)}
              >
                <img src={resolveIcon("cancelCircle")} alt="Delete" />
              </button>
            </div>
          )}
        </form>
      </>
    ));
  };

  const handleUpload = async (event) => {
    if (event) event.preventDefault();

    if (!pdfFile) {
      console.error("No file selected!");
      return;
    }

    const formData = new FormData();
    formData.append("image", pdfFile);

    try {
      setAnalyzeLoading(true);
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_AI_API}/extract`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
            "User-Id": userData?.userId,
            "Supplier-Id": selectedSupplier?.id,
            // organizationid: 29,
            organizationid: userData?.organizationId,
          },
        },
      );

      if (uploadResponse.status === 200) {
        setExtractedData(uploadResponse.data);
        navigate("/analyzedNoCustomerInvoice", {
          state: { extractedData: uploadResponse.data }, // Pass correct data
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const cancelUpload = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_AI_API}/jobs/cancel-all`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
            "User-Id": userData?.userId,
            "Supplier-Id": selectedSupplier?.id,
          },
        },
      );
    } catch (error) {
      console.error("Cancel upload failed:", error);
    }
  };

  // Handler for opening the row info modal
  const handleRowButtonClick = (row) => {
    setSelectedRow(row);
    setShowRowModal(true);
  };

  // Handler for closing the row info modal
  const handleCloseRowModal = () => {
    setSelectedRow(null);
    setShowRowModal(false);
  };

  const rows = [
    {
      title: "invoiceNumber",
      value: "invoiceNumber",
    },
    {
      title: "amountForSupplier",
      value: "amountForSupplier",
    },
    {
      title: "containerNumber",
      value: "containerNumber",
    },
    {
      title: "invoiceDate",
      value: "invoiceDate",
    },
    {
      title: "createdBy",
      value: "createdBy",
    },
    {
      title: "company",
      value: "subCompany",
    },
    {
      title: "print",
      value: "print",
    },
  ];

  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(null);
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "invoiceNumber",
  });

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const searchTitles = useMemo(
    () => [
      {
        title: t("invoiceNumber"),
        value: "invoiceNumber",
        type: "text",
      },
      {
        title: t("amountForSupplier"),
        value: "amountForSupplier",
        type: "moneyV2",
      },
    ],
    [t],
  );

  const {
    selectedSupplier,
    selectedSubCompany,
    loading,
    setLoading,
    currentBalance,
    setSelectedSupplier,
    selectedInvoicesOfMultiContsStatus,
    setSelectedInvoicesOfMultiContsStatus,
    suppliers,
  } = useContext(DataContext);

  const balanceStyle =
    currentBalance?.value >= 0 ? "positive-value" : "negative-value";

  const fetchInvoices = async (resetPage = false) => {
    setLoading(true);

    try {
      setInvoicesLoading(true);

      const params = {
        supplierId:
          selectedSupplier?.id !== "All" ? selectedSupplier?.id : "All",
        subCompanyId:
          selectedSubCompany?.id !== "All" ? selectedSubCompany?.id : undefined,
        type: selectedTitle.value,
        query: searchQuery,
        startDate: startDate,
        endDate: endDate,
        page: resetPage ? 1 : invoicesCurrentPage,
        limit: 10,
        // these for archived endpoint
        userId: userData?.userId,
        organizationId: userData?.organizationId,
        customerId: "All",
      };

      // Remove undefined values
      Object.keys(params).forEach((key) => {
        if (
          params[key] === undefined ||
          params[key] === null ||
          params[key] === ""
        ) {
          delete params[key];
        }
      });

      // Choose endpoint based on status
      const response =
        selectedInvoicesOfMultiContsStatus?.value === "archived"
          ? await getArchivedMultiContainerInvoicesWithouC(params)
          : await getMultiContainerInvoices(params);

      if (response.status === 200) {
        setInvoices(response.data.invoices || []);
        setTotalRecords(response.data.totalInvoices || 0);
        setInvoicesTotalPages(response.data.totalPages || 1);

        // ✅ prevent duplicate fetch caused by auto-setting supplier
        if (!selectedSupplier?.id) {
          skipNextFetchRef.current = true;
          setSelectedSupplier(response?.data?.currentSupplier);
        }

        if (resetPage) {
          setInvoicesCurrentPage(1);
        }

        // Calculate balance from invoices
        const totalBalance =
          response.data.invoices?.reduce((sum, invoice) => {
            return sum + (invoice.amountForSupplier || 0);
          }, 0) || 0;

        setBalances({ amountForSupplier: totalBalance });
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setError("Failed to fetch invoices");
    } finally {
      setInvoicesLoading(false);
      setLoading(false);
    }
  };
  useEffect(() => {

    if (!didInitPageFromUrl.current) {
      const pageFromUrl = parseInt(
        new URLSearchParams(location.search).get("page") || "1",
        10,
      );

      didInitPageFromUrl.current = true;

      if (
        !Number.isNaN(pageFromUrl) &&
        pageFromUrl > 0 &&
        pageFromUrl !== invoicesCurrentPage
      ) {
        setInvoicesCurrentPage(pageFromUrl);
        return;
      }
      // if pageFromUrl === current page, continue to sync+fetch
    }

    // 2️⃣ Sync state → URL (only if different)
    const currentUrlPage = parseInt(
      new URLSearchParams(location.search).get("page") || "1",
      10,
    );

    if (
      Number.isNaN(currentUrlPage) ||
      currentUrlPage !== invoicesCurrentPage
    ) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", String(invoicesCurrentPage));
          return next;
        },
        { replace: true },
      );
    }

    // 3️⃣ Skip the next fetch if it was triggered only by setSelectedSupplier()
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    // 4️⃣ Fetch invoices
    fetchInvoices(false);
  }, [
    invoicesCurrentPage,
    selectedSupplier?.id,
    selectedSubCompany?.id,
    selectedInvoicesOfMultiContsStatus?.value,
    setSearchParams,
    location.search,
  ]);

  // Reset status only on unmount, not on every effect re-run.
  useEffect(() => {
    return () => {
      setSelectedInvoicesOfMultiContsStatus({
        title: "Active",
        value: "active",
      });
    };
  }, [setSelectedInvoicesOfMultiContsStatus]);

  const handleChangeStatus = (status) => {
    const { value } = status;
    setSelectedInvoicesOfMultiContsStatus(status);
    setInvoicesCurrentPage(1);
    setSearchQuery("");
  };

  const handleShowChart = () => {
    setChartModalOpen(true);
  };

  const handleCloseChart = () => {
    setChartModalOpen(false);
  };

  const handleSearchType = async (type) => {
    setSelectedTitle(type);
  };

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }

    let searchBy;

    if (selectedTitle.value === "date") {
      searchBy = selectedDate;
    } else {
      searchBy = query;
    }

    setSearchQuery(searchBy);
    fetchInvoices(true);
  };

  const hiddenBalances = selectedSupplier?.id === "All";

  return (
    <>
      <main className="e-container">
        <div>
          {/*<BackToDashboard*/}
          {/*  title={t("back")}*/}
          {/*  variant="blank"*/}
          {/*  icon={currentDirection === "ltr" ? "previous" : "next"}*/}
          {/*  iconSize={14}*/}
          {/*/>*/}
        </div>

        <div className="invoice-table-container">
          <UserSection
            handleShowChart={handleShowChart}
            page="noCustomerInvoices"
          />

          {showCantAddInvoice && (
            <ErrorBox>
              <p>{t("loginAsUserToCreateInvoice")}</p>
            </ErrorBox>
          )}

          <div className="under-user-section">
            <div className="page-title">{t("invoicesPage")}</div>
            <CustomButton
              elementType={Link}
              title={t("containers")}
              to="/noCustomerContainers"
              icon="container"
            />
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {suppliers.length === 0 ? (
                <div className="full-w-button-container">
                  <div className="column-flex-box flex-center-center">
                    <InformationBox>{t("noSuppliersAddOne")}</InformationBox>
                    <CustomButton
                      title={t("goToSuppliersPage")}
                      minWidth={140}
                      variant="blank"
                      elementType={Link}
                      to={"/viewSuppliersPage"}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="o-column-row-switcher-reversed o-row--horizontal-align_space-between o-row--gap-size_400">
                    {/* create & analyze */}

                    <div className="o-row o-row--gap-size_300">
                      <CustomButton
                        title={t("createInvoice")}
                        icon="add"
                        iconSize={20}
                        elementType={Link}
                        to={
                          localStorage.getItem("userType") === "organization"
                            ? ""
                            : "/createNoCustomerInvoice"
                        }
                        onClick={() => {
                          if (
                            localStorage.getItem("userType") === "organization"
                          ) {
                            setShowCantAddInvoice(true);
                          }
                        }}
                      />

                      {!showBtns && (
                        <CustomButton
                          title={t("analyzeInvoice")}
                          icon="analyze"
                          variant="blank"
                          onClick={show3Buttons}
                          iconSize={18}
                        />
                      )}
                    </div>

                    {/* status dropdown */}
                    <section className="col title-col o-row o-row--horizontal-align_end o-row--vertical-align_center o-row--gap-size_300">
                      <label>{t("invoicesStatus")}: </label>
                      <Dropdown
                        options={[
                          { title: "Active", value: "active" },
                          { title: "Archived", value: "archived" },
                        ]}
                        selected={selectedInvoicesOfMultiContsStatus}
                        setSelected={handleChangeStatus}
                        thick
                      />
                    </section>
                  </div>

                  {showBtns && (
                    <div className="o-row o-row--gap-size_200">
                      <section className="u-margin--top_500 u-margin--bottom_500 full-width">
                        <div className={classes.pdfUploaderSection}>
                          <span>{t("uploadDocs")}: </span>
                          <div className={classes.pdfUploaderSectionActions}>
                            {generateUploadInputs()}
                          </div>
                        </div>
                      </section>

                      {documents.length ? (
                        <AllDocumentsSlider
                          documents={documents}
                          title="All Documents"
                        />
                      ) : null}
                    </div>
                  )}

                  <MultipleSearchFilter
                    options={searchTitles}
                    selected={selectedTitle}
                    setSelected={handleSearchType}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    thick
                    getData={() => {}}
                    handleSearch={handleSearch}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    startDate={startDate}
                    endDate={endDate}
                    flex
                    className="u-margin--top_500 u-margin--bottom_500"
                  />

                  {invoicesLoading && <Loader />}

                  {!invoicesLoading && invoices?.length !== 0 && (
                    <>
                      <section className="table-container">
                        <table className="i-table">
                          <thead className="table-head">
                            <tr>
                              {rows.map((row, index) => {
                                if (
                                  row?.title !== "company" ||
                                  selectedSubCompany?.id === "All"
                                ) {
                                  return (
                                    <th
                                      key={index}
                                      className={`bold-text cell-title ${
                                        row.title === "invoiceNumber"
                                          ? "invoiceNumberHeader"
                                          : ""
                                      }`}
                                    >
                                      {t(row.title)}
                                    </th>
                                  );
                                }
                                return null;
                              })}
                            </tr>
                          </thead>
                          <tbody className="table-body">
                            {invoices?.map((row, index) => (
                              <tr key={index}>
                                <td className="cell sharp-cell text-center">
                                  {selectedInvoicesOfMultiContsStatus?.value !==
                                  "archived" ? (
                                    <CustomLink
                                      to={`/viewNoCustomerInvoice/${row.id}`}
                                      state={{
                                        returnTo:
                                          location.pathname + location.search,
                                      }}
                                    >
                                      <p className="link">
                                        {row?.invoiceNumber}
                                      </p>
                                    </CustomLink>
                                  ) : (
                                    <p className="archived-invoice">
                                      {row?.invoiceNumber}
                                    </p>
                                  )}
                                </td>
                                <td className="cell">
                                  <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center o-row--gap-size_100">
                                    <div className="t-text-standard">
                                      {row?.amountForSupplier}
                                    </div>
                                    <div className="t-bold_bold">
                                      {row?.supplier?.currency}
                                    </div>
                                  </div>
                                </td>
                                <td className="cell">
                                  {row.containers &&
                                  row.containers.length > 0 ? (
                                    row.containers.map((containerName, idx) => (
                                      <div
                                        key={idx}
                                        className="o-column o-column--vertical-align_start o-column--gap-size_200 u-margin--bottom_300"
                                      >
                                        <span className="notes-bold">
                                          {t("container")} {idx + 1} :
                                        </span>
                                        <span>{containerName}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <span className="o-row t-text-align_center">
                                      {t("noAddedContainers")}
                                    </span>
                                  )}
                                </td>
                                <td
                                  className="cell"
                                  style={{ height: "50px", minWidth: "145px" }}
                                >
                                  {row.date
                                    ? new Date(row.date)
                                        .toISOString()
                                        .split("T")[0]
                                    : ""}
                                </td>
                                <td
                                  className="cell"
                                  style={{ height: "50px", minWidth: "145px" }}
                                >
                                  {row?.createdBy}
                                </td>
                                {selectedSubCompany?.id === "All" && (
                                  <td
                                    className="cell"
                                    style={{
                                      height: "50px",
                                      minWidth: "145px",
                                    }}
                                  >
                                    <span className="o-row t-text-align_center">
                                      {row?.subCompany?.name || "-"}
                                    </span>
                                  </td>
                                )}
                                <td className="cell">
                                  <CustomButton
                                    variant="blank"
                                    title={t("ViewInfo")}
                                    onClick={() => handleRowButtonClick(row)}
                                    icon="info"
                                    iconSize={15}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </section>
                      <section className="pagination">
                        <div className="pagination-buttons">
                          <CustomButton
                            variant="blank"
                            title={t("previous")}
                            onClick={() =>
                              setInvoicesCurrentPage((prev) => prev - 1)
                            }
                            disabled={invoicesCurrentPage === 1}
                            icon={
                              currentDirection === "ltr" ? "previous" : "next"
                            }
                            iconSize={15}
                            mobileEffect={true}
                          />
                          <span className="pagination-location">
                            {t("page")}: {invoicesCurrentPage} -{" "}
                            {invoicesTotalPages}
                          </span>
                          <CustomButton
                            title={t("next")}
                            variant="blank"
                            onClick={() =>
                              setInvoicesCurrentPage((prev) => prev + 1)
                            }
                            disabled={
                              invoicesCurrentPage === invoicesTotalPages
                            }
                            icon={
                              currentDirection === "ltr" ? "next" : "previous"
                            }
                            iconSize={15}
                            reverseIcon
                            mobileEffect={true}
                          />
                        </div>

                        <div className="pagination-buttons">
                          <span className="pagination-location">
                            {t("totalRecords")}: {totalRecords}
                          </span>
                        </div>
                      </section>
                      {!hiddenBalances && selectedSupplier?.id && (
                        <div className="balances margin-inline o-column o-column--vertical-align_end o-column--gap-size_400 u-margin--top_400">
                          <div className="balance">
                            <span>{t("supplierCurrentBalance")}: </span>
                            <span className={balanceStyle}>
                              {!selectedSupplier?.currency ||
                              selectedSupplier?.currency === "N/A" ? (
                                t("noSupplierSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>
                                    {toMoney(balances?.amountForSupplier)}
                                  </span>
                                  <span>{selectedSupplier?.currency}</span>
                                </div>
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {!loading && !invoicesLoading && invoices?.length === 0 && (
                    <InformationBox>
                      <p>{t("noTransactions")}</p>
                    </InformationBox>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Render the ChartModal component */}
      <ChartModal
        open={chartModalOpen}
        onClose={handleCloseChart}
        data={invoices}
      />

      {/* Render the Printing modal */}
      {showRowModal && selectedRow && (
        <NoCustomerInformationModal
          // selectedRow={selectedRow}
          handleClose={handleCloseRowModal}
          invoiceId={selectedRow.id}
        />
      )}
    </>
  );
};

export default NoCustomerInvoices;
